import React from "react";

/**
 * [PONPISH] 개인정보 처리방침 (React + TailwindCSS)
 * - Tailwind 설정이 된 환경을 전제로 작성합니다.
 * - 좌측 고정 목차(>=lg), 모바일 접이식 목차, 섹션 앵커 스크롤, 가독성 위주의 레이아웃 구성.
 * - 정책 텍스트는 사용자가 제공한 최신 본문(최종 업데이트: 2025-05-30)을 반영했습니다.
 */

const sections = [
  {
    id: "intro",
    title: "개요",
    body: (
      <p>
        주식회사 챙기고(대표자: 박태홍, 이하 “회사”)는 「개인정보 보호법」 제30조에 따라 정보주체의
        개인정보를 보호하고 고충을 신속·원활하게 처리하기 위해 본 개인정보 처리방침을 수립·공개합니다.
      </p>
    ),
  },
  {
    id: "purpose",
    title: "제1조 (개인정보의 처리 목적)",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>회원 가입 및 관리:</strong> 간편로그인 제공, 회원 식별·본인·연령 확인, 부정이용 방지 등.
        </li>
        <li>
          <strong>재화 또는 서비스 제공:</strong> 컬러렌즈 상품 및 관련 서비스 제공, 결제·픽업·주문 확인 등 계약 이행.
        </li>
        <li>
          <strong>결제 및 정산:</strong> 대금 결제·환불·전자금융거래 기록 보관(결제정보는 PG사에서 처리, 회사는 저장하지 않음).
        </li>
        <li>
          <strong>고객 문의 응대 및 민원 처리:</strong> 고객센터 운영, 문의 대응 및 처리 결과 안내.
        </li>
        <li>
          <strong>법령 상 의무 준수:</strong> 전자상거래, 세무·회계 등 관련 법령 준수 목적의 처리.
        </li>
        <li>
          <strong>기타 부수 목적:</strong> 통계·분석, 서비스 개선, 이벤트·마케팅(사전 동의 시) 등.
        </li>
      </ul>
    ),
  },
  {
    id: "items-method",
    title: "제2조 (수집 항목 및 수집 방법)",
    body: (
      <div className="space-y-3">
        <ol className="list-decimal pl-5 space-y-2">
          <li>
            <strong>수집 항목</strong>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                <strong>회원가입/인증(필수):</strong> 이름, 생년월일, 휴대전화번호, (간편로그인 시) SNS 제공 고유 ID, 이름/닉네임, 프로필, 선택 시 이메일.
              </li>
              <li>
                <strong>주문/배송(필수):</strong> 상품정보(옵션·수량 등), 지정 안경점 정보, 픽업일시, 필요 시 렌즈 도수 등 추가 옵션.
              </li>
              <li>
                <strong>결제(필수):</strong> 결제수단·거래정보(승인번호, 금액, 일시 등). 카드번호 등 민감 결제정보는 PG사 보관.
              </li>
              <li>
                <strong>추가(선택):</strong> 배송 요청사항, 설문 응답 등. 미제공 시 기본 서비스 이용 제한 없음.
              </li>
              <li>
                <strong>자동 수집:</strong> 기기정보(OS/모델/앱버전), IP, 쿠키·세션, 방문·이용 기록, 불량 이용 기록 등.
              </li>
            </ul>
          </li>
          <li>
            <strong>수집 방법</strong>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>직접 제공:</strong> 가입, 주문서, 고객센터 문의 등에서 입력.</li>
              <li><strong>연동 제공:</strong> 네이버·카카오·구글·애플 간편로그인 동의 범위 내 제공.</li>
              <li><strong>생성 정보:</strong> 로그·쿠키·모바일 식별값 등 자동 수집.</li>
              <li><strong>기타:</strong> 이벤트, 설문, 제휴사 제공 등 정당 절차를 통한 수집.</li>
            </ul>
          </li>
        </ol>
      </div>
    ),
  },
  {
    id: "retention",
    title: "제3조 (보유 및 이용 기간)",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>회원 계정:</strong> 탈퇴 시까지. 탈퇴·자격상실 시 즉시 파기 또는 분리보관(재가입 남용 방지 목적 최소 정보 분리보관 가능).
        </li>
        <li>
          <strong>주문/거래:</strong> 전자상거래법 기준 <strong>계약·청약철회 5년, 대금결제·공급 5년, 분쟁처리 3년, 표시·광고 6개월</strong> 보관.
        </li>
        <li>
          <strong>전자금융거래 기록:</strong> 전자금융거래법에 따라 <strong>5년</strong> 보관.
        </li>
        <li>
          <strong>통신비밀보호법 로그:</strong> 접속기록 등 <strong>3개월</strong> 보관.
        </li>
        <li>
          <strong>상담/AS:</strong> 처리 완료 후 <strong>3년</strong> 보관.
        </li>
        <li>
          <strong>장기 미이용(휴면) 회원:</strong> 1년 미이용 시 분리보관, 최대 <strong>4년</strong> 경과 시 파기(사전 통지 후 조치).
        </li>
      </ul>
    ),
  },
  {
    id: "third-party",
    title: "제4조 (개인정보의 제3자 제공)",
    body: (
      <div className="space-y-3">
        <p>
          원칙적으로 정보주체 동의 없이 목적 범위를 초과하여 제3자 제공하지 않습니다. 예외적으로 동의가 있거나 법령에 따른 경우 제공할 수 있습니다.
        </p>
        <div className="rounded-xl border p-4 bg-gray-50">
          <p className="font-medium">픽업 서비스 제공을 위한 제공</p>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-sm">
            <li>
              <strong>제공받는 자:</strong> 고객이 선택한 지정 안경점
            </li>
            <li>
              <strong>목적:</strong> 배송·보관, 본인확인 후 상품 교부, 수령 확인 등 사후 지원
            </li>
            <li>
              <strong>항목:</strong> 주문자 이름, 휴대전화, 주문번호/상품내역, 결제금액, 픽업 예정일시
            </li>
            <li>
              <strong>보유 기간:</strong> 픽업 완료 후 법정기간 범위 내 보관, 목적 달성 후 최대 15일 이내 삭제
            </li>
          </ul>
          <p className="mt-2 text-sm">※ 동의 거부 가능하나, 필수 정보 미제공 시 배송·픽업 이용이 어려울 수 있습니다.</p>
          <p className="mt-1 text-sm">※ 간편로그인 시 인증에 필요한 정보만 교환하며 비밀번호 등은 제공하지 않습니다.</p>
        </div>
      </div>
    ),
  },
  {
    id: "outsourcing",
    title: "제5조 (개인정보 처리의 위탁)",
    body: (
      <div className="space-y-2">
        <p>
          원활한 서비스 제공을 위해 아래 업무를 수탁자에게 위탁하며, 재위탁 제한·보호조치·감독·손해배상 등을 계약에 명시합니다.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>결제 처리:</strong> 토스페이먼츠㈜ – 결제대행 및 지급 처리(토스페이)
          </li>
          <li>
            <strong>물류 배송:</strong> 택배사(예: CJ대한통운) – 상품 배송 및 추적
          </li>
          <li>
            <strong>알림 발송:</strong> 문자·알림톡 발송 대행사 – 주문/배송 안내 발송
          </li>
          <li>
            <strong>고객상담:</strong> 콜센터 대행사 – 고객 문의 응대
          </li>
        </ul>
        <p className="text-sm text-gray-600">
          ※ 수탁자 또는 위탁 업무 변경 시 본 방침에 반영하고 필요한 경우 사전 고지·동의를 받습니다.
        </p>
      </div>
    ),
  },
  {
    id: "minor",
    title: "제6조 (만 14세 미만 아동의 보호)",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>만 14세 미만의 회원가입·이용을 허용하지 않습니다(만 14세 이상만 이용 가능).</li>
        <li>무단 수집이 확인되면 지체 없이 삭제 및 이용 중지 등 조치합니다.</li>
        <li>허위 정보 제공으로 발생하는 책임은 해당 이용자 또는 법정대리인이 부담합니다.</li>
      </ul>
    ),
  },
  {
    id: "rights",
    title: "제7조 (정보주체의 권리·의무 및 행사방법)",
    body: (
      <div className="space-y-2">
        <p className="font-medium">권리</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>개인정보 열람 요구</li>
          <li>개인정보 정정·삭제 요구(타 법령상 보존 대상은 제외)</li>
          <li>개인정보 처리정지 요구(법령상 불가한 경우 제외)</li>
          <li>동의 철회 및 회원탈퇴</li>
        </ul>
        <p className="font-medium mt-3">행사방법</p>
        <p>
          서면·전자우편·고객센터 등을 통해 본인확인 후 지체 없이 조치합니다. 대리인 행사 시 위임장 양식 제출이 필요합니다.
        </p>
        <p className="text-sm text-gray-700">
          ※ 열람·정지 요청은 통상 10일 이내 조치하며, 제3자 제공 사실이 있는 경우 정정·삭제 결과를 통지합니다.
        </p>
        <p className="font-medium mt-3">의무</p>
        <p>
          정확한 정보 유지, 타인정보 도용 금지, 계정·비밀번호 보안 유지 등 개인정보 보호를 위한 관리 조치를 준수합니다.
        </p>
      </div>
    ),
  },
  {
    id: "destroy",
    title: "제8조 (개인정보의 파기 절차 및 방법)",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>파기 절차:</strong> 보유기간 경과 또는 목적 달성 시 즉시 파기. 법정 보존 필요 시 별도 분리 저장·관리.
        </li>
        <li>
          <strong>파기 방법:</strong> 전자파일은 복구 불가능 방식으로 영구 삭제, 문서는 분쇄 또는 소각.
        </li>
      </ul>
    ),
  },
  {
    id: "security",
    title: "제9조 (안전성 확보 조치)",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>관리적:</strong> 내부관리계획, 최소권한, 임직원 교육·서약, 권한 차등 부여.
        </li>
        <li>
          <strong>기술적:</strong> 접근권한 관리, 접근통제, 방화벽·IDS, 전송구간 암호화(SSL), 중요 데이터 암호화, 백신 운영, 취약점 점검.
        </li>
        <li>
          <strong>물리적:</strong> 전산실·자료보관실 출입통제, 잠금장치 관리.
        </li>
        <li>
          <strong>내부 점검:</strong> 정기 자체 점검, 접근 로그 모니터링·감사, 사고 대응 절차 운영.
        </li>
      </ul>
    ),
  },
  {
    id: "dpo",
    title: "제10조 (개인정보 보호책임자 및 담당부서)",
    body: (
      <div className="space-y-2">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>개인정보 보호책임자:</strong> 성명: 홍승관 / 직책: 본부장 / 연락처: 070-4271-0801, tmdrhks852@ponpish.kr
          </li>
          <li>
            <strong>개인정보 보호 담당부서:</strong> 고객지원팀 / 연락처: 070-4271-0801, cs@ponpish.kr
          </li>
        </ul>
        <p className="text-sm text-gray-700">문의 시 신속하고 성실하게 대응합니다.</p>
      </div>
    ),
  },
  {
    id: "open-dept",
    title: "제11조 (열람청구 접수·처리 부서)",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>부서:</strong> 고객지원팀 / <strong>연락처:</strong> 070-4271-0801, cs@ponpish.kr
        </li>
      </ul>
    ),
  },
  {
    id: "remedy",
    title: "제12조 (권익침해 구제방법)",
    body: (
      <div className="space-y-2">
        <p>아래 기관에 피해 구제·상담을 요청할 수 있습니다(회사와 별개 기관).</p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>
            개인정보 분쟁조정위원회(개인정보보호위원회): www.kopico.go.kr / 1833-6972
          </li>
          <li>개인정보 침해신고센터(KISA): privacy.kisa.or.kr / 118</li>
          <li>대검찰청 사이버범죄수사단: www.spo.go.kr / 1301</li>
          <li>경찰청 사이버안전국: cyberbureau.police.go.kr / 182</li>
        </ul>
        <p className="text-sm text-gray-700">
          또한 행정심판법 절차에 따라 행정심판을 청구할 수 있습니다(중앙행정심판위원회: www.simpan.go.kr).
        </p>
      </div>
    ),
  },
  {
    id: "change",
    title: "제13조 (처리방침의 변경)",
    body: (
      <ul className="list-disc pl-5 space-y-2">
        <li>본 방침은 <strong>2025년 5월 30일</strong>부터 적용합니다.</li>
        <li>내용 추가·삭제·수정 시 최소 7일 전(중대한 변경 30일 전) 사전 공지합니다.</li>
        <li>이전 버전은 웹/앱 내 버전별로 열람 가능하도록 공개합니다.</li>
      </ul>
    ),
  },
];

function Toc({ sections }: { sections: { id: string; title: string }[] }) {
  return (
    <nav className="hidden lg:block sticky top-24 h-[calc(100vh-6rem)] overflow-auto pr-6">
      <h2 className="text-sm font-semibold text-black mb-3">목차</h2>
      <ol className="space-y-2 text-sm">
        {sections.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="block rounded-md px-2 py-1 hover:bg-gray-100 transition text-black"
            >
              {s.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function MobileToc({ sections }: { sections: { id: string; title: string }[] }) {
  return (
    <details className="lg:hidden mb-4 rounded-xl border bg-white shadow-sm">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-black">목차 열기</summary>
      <div className="border-t px-4 py-3">
        <ol className="space-y-2 text-sm">
          {sections.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="block rounded-md px-2 py-1 hover:bg-gray-100 text-black">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}

export default function PonpishPrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* 컨테이너 */}
      <main className="mx-auto max-w-6xl px-2 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8">
          <Toc sections={sections.map(({ id, title }) => ({ id, title }))} />

          <article className="min-w-0">
            <div className="rounded-2xl border bg-white p-6">
              <h1 className="text-2xl font-bold tracking-tight text-black">[PONPISH] 개인정보 처리방침</h1>
              <p className="mt-2 mb-4 text-sm text-gray-600">시행일: 2025년 8월 1일</p>

              <MobileToc sections={sections.map(({ id, title }) => ({ id, title }))} />

              <div className="divide-y">
                {sections.map((s) => (
                  <section id={s.id} key={s.id} className="scroll-mt-24 py-6">
                    <h2 className="text-lg font-semibold mb-3 text-black">{s.title}</h2>
                    <div className="space-y-3 leading-7 text-black">{s.body}</div>
                  </section>
                ))}
              </div>

              <footer className="mt-6 border-t pt-4 text-xs text-gray-600">
                법령·서비스 변경에 따라 본 문서는 갱신될 수 있습니다.
              </footer>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}
